/**
 * Responsible for all communication with the server.
 *
 * Will display a user provided login window to allow.
 */

import $ from "jquery";
import * as _ from "underscore";

import * as chromeExtensionRequestClient from "chromeExtensionRequestClient";
import * as googleAuth from "googleAuth";
import * as localAPI from "localAPI";
import * as notificationBar from "notificationBar";
import * as readlangLocalStorage from "readlangLocalStorage";
import * as sharedConfig from "shared/sharedConfig";

class ReadlangClient {
  private user: any;
  private checkingForLogout: boolean;
  private baseURL: string;
  private globalOptions: {
    [key: string]: any;
    unauthorizedHandler?: () => void;
  } = {};
  private checkTimeout?: ReturnType<typeof setTimeout>;
  private promptForCredentials: (options: any) => void = () => {};

  constructor(baseURL: string) {
    this.user = null;
    this.checkingForLogout = false;
    this.baseURL = baseURL;
    // This conditional is a hacky fix - this module shouldn't be loaded at all in the embedded reader
    if (/localhost|readlang/.test(window.location.host)) {
      $(window).focus(() => {
        clearTimeout(this.checkTimeout);
        this.checkTimeout = setTimeout(() => {
          this.checkForLogoutInOtherTab();
        }, 30);
      });
    }
  }

  public setSignupModal(signupModal: any) {
    this.promptForCredentials = signupModal.show;
  }

  public setOption(key: string, value: any) {
    this.globalOptions[key] = value;
  }

  private checkForLogoutInOtherTab() {
    if (this.checkingForLogout) {
      return;
    }
    this.checkingForLogout = true;

    readlangLocalStorage.getItem("loggedInEmail", (loggedInEmail) => {
      this.checkingForLogout = false;
      if (
        this.user &&
        !this.user.isNew() &&
        (this.user.has("email") || loggedInEmail) &&
        this.user.get("email") !== loggedInEmail
      ) {
        // TODO: replace with nicer alert
        //alert('You have logged out in another tab, this tab will now refresh');
        window.location.reload();
      }
    });
  }

  async handleError(status, data, errorCallback) {
    const that = this;

    if (status === 401) {
      if (that.globalOptions.unauthorizedHandler) {
        that.globalOptions.unauthorizedHandler();
      } else {
        await readlangLocalStorage.setItem("localMode", true);
        window.location.reload();
        return;
      }
    } else if (status === 0) {
      notificationBar.showMessage({
        htmlMessage:
          'Connection to server lost. Please check you are connected to the internet, then <a href="' +
          window.location.href +
          '" data-bypass>reload this page</a>.',
        above: true,
        time: 3600 * 1000,
        element: $("body"),
      });
    } else if (status >= 500) {
      notificationBar.showMessage({
        htmlMessage:
          "Error communicating with server (" +
          status +
          ").<br><br>" +
          'Try <a data-bypass href="' +
          window.location.href +
          '">reloading the page</a>.',
        above: true,
        time: 3600 * 1000,
        element: $("body"),
      });
      console.error(
        "Server error response: %s (%s)\ntext status: %s\nresponse text: %s",
        // errorThrown,
        status,
        data
        // responseText
      );
    } else {
      if (errorCallback) {
        errorCallback({ status, data });
      } else {
        // show notification to user - advise page refresh
        console.error(
          "Remote request error: %s (%s)\ntext status: %s\nresponse text: %s",
          // errorThrown,
          status
          // jqXHR.responseText
        );

        notificationBar.showMessage({
          htmlMessage:
            "Unexpected error communicating with server (" +
            status +
            '). <a href="' +
            window.location.href +
            '" data-bypass>Reload page</a>',
          above: true,
          time: 3600 * 1000,
          element: $("body"),
        });
      }
    }
  }

  public async remoteRequest(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    data: any,
    callback: any,
    errorCallback: any,
    jQueryOptions?: any,
    retryAttempt = 0
  ) {
    const that = this;
    this.checkForLogoutInOtherTab();

    const ajaxOptions = _.extend(
      {
        type: method,
        url: this.baseURL + path,
        data: data,
        dataType: "json",
        xhrFields: {
          withCredentials: true,
        },
        success: (responseData, textStatus, jsXHR) => {
          // Check for API compatibility version mismatch
          const serverApiVersion = jsXHR.getResponseHeader(
            "Readlang-API-Version"
          );
          if (
            serverApiVersion &&
            serverApiVersion >
              sharedConfig.clientServerCompatibilityVersion.toString()
          ) {
            // Force a full page reload to ensure the client is up to date
            window.location.reload();
            return;
          }

          // TODO: dismiss notification ONLY if it's the server error one!
          //notificationBar.dismiss();
          callback(responseData, textStatus, jsXHR);
        },
        error: (jqXHR, textStatus) => {
          const maxRetryAttempts = 3;
          if (jqXHR.status === 0 && retryAttempt < maxRetryAttempts) {
            // Retry after 15s in case of network error
            setTimeout(
              () => {
                that.remoteRequest(
                  method,
                  path,
                  data,
                  callback,
                  errorCallback,
                  jQueryOptions,
                  retryAttempt + 1
                );
              },
              (1 + retryAttempt) * 5 * 1000
            );
            return;
          }

          that.handleError(
            jqXHR.status,
            jqXHR.responseJSON ?? jqXHR.responseText ?? textStatus,
            errorCallback
          );
        },
        cache: false,
      },
      jQueryOptions
    );

    if (/^(POST|PATCH|PUT|DELETE)$/.test(method)) {
      ajaxOptions.processData = false;
      ajaxOptions.data = JSON.stringify(data);
      ajaxOptions.contentType = "application/json";
    }

    if (window.READLANG.chromeExtension) {
      // Always use chromeExtensionRequestClient for requests from chrome extension, as it can
      // handle requests even when third party cookies are disabled

      // send message to background page to do request
      let response;

      try {
        response = await chromeExtensionRequestClient.request({
          method,
          url: this.baseURL + path,
          data,
        });
      } catch (error) {
        console.log("handling error: ", error);
        this.handleError(error.status, error.data, errorCallback);
        throw error;
      }
      if (response.status === 200) {
        callback(response.data, response.status);
      } else {
        this.handleError(response.status, response.data, errorCallback);
      }
    } else {
      $.ajax(ajaxOptions);
    }
  }

  /**
   * IMPORTANT: if errorCallback is provided, promises will not be rejected on failure
   */
  public request(
    method: string,
    path: string,
    data: any,
    callback?: any,
    errorCallback?: any,
    jQueryOptions?: any
  ) {
    return new Promise<{ data; textStatus; jsXHR }>((resolve, reject) =>
      (async () => {
        const successCallbackWrapper = (responseData, textStatus, jsXHR) => {
          callback?.(responseData, textStatus, jsXHR);
          resolve({ data: responseData, textStatus, jsXHR });
        };
        const errorCallbackWrapper = (jqXHR, textStatus, errorThrown) => {
          if (errorCallback) {
            errorCallback?.(jqXHR, textStatus, errorThrown);
          } else {
            reject({ jqXHR, textStatus, errorThrown });
          }
        };

        if (
          await localAPI.request(
            method,
            path,
            data,
            successCallbackWrapper,
            errorCallbackWrapper,
            // XXX This is so convoluted! Why do we need to pass remote request to localAPI??
            (_method, _path, _data, _successCallback, _errorCallback) => {
              this.remoteRequest(
                _method as "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
                _path,
                _data,
                _successCallback,
                _errorCallback,
                jQueryOptions
              );
            }
          )
        ) {
          return;
        }

        this.remoteRequest(
          method as "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
          path,
          data,
          successCallbackWrapper,
          errorCallbackWrapper,
          jQueryOptions
        );
      })()
    );
  }

  public login() {
    // try logging in
    this.promptForCredentials({
      models: { user: this.user },
    });
  }

  public setUserModel(_user: any) {
    this.user = _user;
  }

  public async isLocalMode() {
    return await localAPI.isLocalMode();
  }

  public async setLocalMode(localMode: boolean) {
    await localAPI.setLocalMode(localMode);
  }

  public logout(callback?: () => void) {
    this.request("POST", "/api/logout", {}, async () => {
      await readlangLocalStorage.clearPromise();
      googleAuth.signOut();
      if (callback) {
        callback();
      } else {
        window.location.href = "/";
      }
    });
  }

  public fetchAuthenticatedState(callback: any) {
    $.ajax({
      type: "GET",
      url: this.baseURL + "/api/isAuthenticated",
      xhrFields: {
        withCredentials: true,
      },
      // Never pass an async callback directly to success or error since it it unreliable. Instead,
      // wrap it in a synchronous function
      success: () => {
        (async () => {
          await this.setLocalMode(false);
          callback();
        })();
      },
      error: () => {
        (async () => {
          await this.setLocalMode(true);
          callback();
        })();
      },
    });
  }
}

// use https for all requests
const protocol = /^(localhost|192\.168\.1\..*|10\.102\.80\.232)$/.test(
  window.location.hostname
)
  ? "http://"
  : "https://";

export default new ReadlangClient(protocol + window.location.hostname);
